import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Compass, Heart, Lightbulb, Shield, Users, Zap, ArrowRight } from "lucide-react";
import PageWrapper from "@/components/PageWrapper";
import SectionHeading from "@/components/SectionHeading";
import AnimatedCounter from "@/components/AnimatedCounter";
import Particles from "@/components/Particles";

const VALUES = [
  { icon: Heart, title: "Craft", body: "We obsess over the details — pixels, performance, prose." },
  { icon: Shield, title: "Trust", body: "Clear communication, honest timelines, durable code." },
  { icon: Lightbulb, title: "Curiosity", body: "We ask why before we ship how. Always." },
  { icon: Zap, title: "Speed", body: "Decisive teams ship faster — we move with intent." },
  { icon: Users, title: "Partnership", body: "We act as an extension of your in-house team." },
  { icon: Compass, title: "Outcomes", body: "We measure success by what your business achieves." },
];

const TEAM = [
  {
    name: "Anas Ansari",
    roles: ["Managing Partner", "Head of Technology (CTO)"],
    img: "https://ui-avatars.com/api/?name=Anas+Ansari&size=800&background=1e3a5f&color=ffffff&bold=true&font-size=0.4",
  },
  {
    name: "Rohit Baptist Rodrigues",
    roles: ["Managing Partner", "Head of Business Development & Sales"],
    img: "/rohit.jpeg",
  },
  {
    name: "Richa Rodrigues",
    roles: ["Graphic Designer"],
    img: "/richa.jpeg",
  },
];

export default function About() {
  return (
    <PageWrapper>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh" aria-hidden />
        <Particles count={24} />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-20 md:pt-28 pb-20 md:pb-32">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-400">
            About Coding Solutions
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mt-5 font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight text-white max-w-5xl"
          >
            Building Digital <br className="hidden md:block" />
            Experiences <span className="text-gradient-accent">Since Day One.</span>
          </motion.h1>
          <p className="mt-7 max-w-2xl text-zinc-400 text-base md:text-lg leading-relaxed">
            We&apos;re a multidisciplinary studio of designers, engineers and
            strategists building premium web products for ambitious brands across
            the globe.
          </p>
        </div>
      </section>

      {/* STORY / MISSION / VISION */}
      <section className="relative py-24 md:py-32 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="Our story"
              title="Born from a love for the craft of the web."
              description="Coding Solutions started as a small studio building Shopify storefronts for D2C founders. Today, we partner with brands across Europe, Asia and Australia on websites, ecommerce platforms and custom web applications — but the obsession remains the same: building experiences people genuinely enjoy using."
            />
          </div>
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 backdrop-blur-md">
              <p className="text-xs uppercase tracking-[0.25em] text-blue-400">
                Mission
              </p>
              <h3 className="font-display text-2xl text-white mt-3">
                Help businesses grow through beautifully crafted, high-performance web platforms.
              </h3>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 backdrop-blur-md">
              <p className="text-xs uppercase tracking-[0.25em] text-blue-400">
                Vision
              </p>
              <h3 className="font-display text-2xl text-white mt-3">
                Become the trusted digital partner for premium brands worldwide.
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative py-20 md:py-24 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {[
            { v: 100, s: "+", l: "Projects Delivered" },
            { v: 15, s: "+", l: "Industries" },
            { v: 10, s: "+", l: "Countries" },
            { v: 98, s: "%", l: "Satisfaction" },
          ].map((s, i) => (
            <div key={s.l} className="border-l border-white/10 pl-5">
              <div className="font-display text-4xl md:text-5xl text-white">
                <AnimatedCounter value={s.v} suffix={s.s} data-testid={`about-stat-${i}`} />
              </div>
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                {s.l}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="relative py-24 md:py-32 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <SectionHeading
            eyebrow="What we stand for"
            title="Our values guide every decision."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className="rounded-3xl border border-white/10 bg-white/[0.02] p-7 backdrop-blur-md hover:border-blue-500/40 transition-colors"
                  data-testid={`value-card-${i}`}
                >
                  <div className="w-11 h-11 grid place-items-center rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400">
                    <Icon size={18} />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl text-white mt-5">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-zinc-400 leading-relaxed">{v.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="relative py-24 md:py-32 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <SectionHeading
            eyebrow="The team"
            title="Senior, hands-on, multidisciplinary."
            description="No layers of account managers. You work directly with the people building your product."
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
            {TEAM.map((m, i) => (
              <motion.div
                key={m.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                data-testid={`team-member-${i}`}
                className="group"
              >
                <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[3/4]">
                  <img
                    src={m.img}
                    alt={m.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                </div>
                <h3 className="mt-4 font-display text-lg text-white">{m.name}</h3>
                {m.roles.map((r) => (
                  <p key={r} className="text-sm text-zinc-400 leading-snug">{r}</p>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="relative py-24 md:py-32 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <SectionHeading
            eyebrow="Why Coding Solutions"
            title="A studio built for what's next."
            description="We've spent years refining what makes a website work — performance, accessibility, conversion, story. We bring all of it to every engagement."
          />
          <ul className="space-y-5">
            {[
              "Senior team — no juniors hidden behind sales decks.",
              "Performance-first engineering (Core Web Vitals you can be proud of).",
              "Design that converts — research-backed, not decorative.",
              "Transparent process and predictable timelines.",
              "Ongoing partnership beyond launch.",
            ].map((p, i) => (
              <li
                key={p}
                className="flex gap-4 items-start"
                data-testid={`why-us-${i}`}
              >
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400" />
                <p className="text-zinc-300 text-base md:text-lg leading-relaxed">{p}</p>
              </li>
            ))}
          </ul>
        </div>
        <div className="mx-auto max-w-7xl px-6 md:px-10 mt-16">
          <Link
            to="/contact"
            data-testid="about-cta-link"
            className="inline-flex items-center gap-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium px-6 py-3 transition-colors"
          >
            Work With Us <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </PageWrapper>
  );
}
